import React, { useMemo, useState } from "react";
import KeisyImg from "../candidates/keisy1.jpeg";
import DanielaImg from "../candidates/danielaU.jpg";
import SantiagoImg from "../candidates/santiago.jpg";
import TomasImg from "../candidates/tomas.jpg";

/* ─── IMAGE PLACEHOLDERS (swap with real imports in your repo) ─── */
const HEADSHOTS = {
  "Keisy": KeisyImg,
  "Daniela": DanielaImg,
  "Santiago": SantiagoImg,
  "Tomas": TomasImg,
};
 
const PROFILE_LINKS = {
  "Keisy": "https://jobs.aiarchitech.com/client/candidates/keisy",
  "Daniela": "https://jobs.aiarchitech.com/client/candidates/danielau",
  "Santiago": "https://jobs.aiarchitech.com/client/candidates/santiago",
  "Tomas": "https://jobs.aiarchitech.com/client/candidates/tomas",
};
 
/* ═══════════════════════════════════════════════════════════════════
   SECTION 1 — RANKED OVERVIEW
   ═══════════════════════════════════════════════════════════════════ */
 
const rankedOverview = [
  {
    rank: 1,
    name: "Keisy",
    title: "Most complete content-to-subscriber pipeline builder",
    summary:
      "Keisy is the candidate who most naturally thinks the way this role needs someone to think. Every answer she gave came back to the same place: how do we get people off social media and onto the email list? She has 5+ years of hands-on DTC content work, she's personally created short-form video, designed carousels, scripted hooks, and built organic funnels that moved people from discovery to email to purchase. She's also the only person in this group who has actually worked with products that can't run paid ads, which is exactly the situation Frizo and LGO are in. Her production quality is solid, not cinematic, and her overall alignment with what this role needs day to day is the clearest in the group.",
  },
  {
    rank: 2,
    name: "Daniela",
    title: "Strongest brand and visual identity builder",
    summary:
      "Daniela brings the strongest brand and visual identity skills in this group. She's built 13 brand identities from scratch, managed 15+ client accounts at a North American agency, and delivered a measurable 45% engagement increase through data-driven content optimization. She understands funnels and knows the three metrics that matter here. Her design thinking is exactly what Frizo and LGO need to build a recognizable brand aesthetic from zero. She's less experienced on the video-first content creation side, so her scripting and hook writing are areas worth exploring in conversation to get a better sense of her depth there.",
  },
  {
    rank: 3,
    name: "Santiago",
    title: "Most metric-driven operator with highest upside",
    summary:
      "Santiago is the youngest candidate in this group and the most metric-driven. He grew views by 406%, reach by 544%, and followers by 32%, all tracked through weekly KPI reports. What stands out is how naturally he thinks about email conversion, orienting his entire strategy around getting people onto the email list before anyone asked him about it. He also brings hands-on ManyChat lead funnel experience with keyword triggers and automated routing. He's earlier in his career than the other three candidates and his production tools are still developing, but his instincts around the metrics that define this role are sharp. At $18 per hour, he offers a strong combination of conversion thinking, automation capability, and room to grow.",
  },
  {
    rank: 4,
    name: "Tomas",
    title: "Strongest AI and automation capability",
    summary:
      "Tomas is a smart, articulate professional who knows how to build content systems and AI-powered workflows better than anyone else in this group. He co-founded a marketing agency, built automated content pipelines using n8n, and thinks in structured frameworks. His background is primarily in B2B content strategy, so the DTC social content production side of this role, editing TikToks, designing carousels, publishing platform-native content daily, would be a newer area for him. His AI and automation expertise is genuinely impressive and could become a significant asset as the content operation scales and the need for automated workflows grows.",
  },
];
 
/* ═══════════════════════════════════════════════════════════════════
   SECTION 2 — SCORING DIMENSIONS & SCORES
   ═══════════════════════════════════════════════════════════════════ */
 
const dimensions = [
  { id: 1, label: "Content Production & Visual Identity" },
  { id: 2, label: "Content-to-Subscriber Pipeline Thinking" },
  { id: 3, label: "Full-Funnel Content Strategy" },
  { id: 4, label: "Scripting, Hook Writing & Copywriting" },
  { id: 5, label: "Founder Coaching & On-Camera Direction" },
  { id: 6, label: "Competitive Research & Cross-Category Intelligence" },
  { id: 7, label: "Metrics Accountability & Performance Optimization" },
  { id: 8, label: "Regulated/Restricted-Ad Industry Readiness" },
  { id: 9, label: "AI Tools & Workflow Efficiency" },
];
 
const scores = {
  "Keisy":    [8, 9, 9, 8, 8, 8, 9, 9, 7],
  "Daniela":  [8, 6, 7, 5, 6, 7, 7, 4, 6],
  "Santiago": [5, 8, 7, 6, 7, 6, 8, 5, 7],
  "Tomas":    [3, 6, 6, 5, 6, 6, 5, 4, 9],
};
 
/* ═══════════════════════════════════════════════════════════════════
   SECTION 3 — CANDIDATE COMPARISONS
   ═══════════════════════════════════════════════════════════════════ */
 
const comparisons = [
  {
    sectionTitle: "Keisy",
    items: [
      {
        heading: "Compared to Daniela",
        body: "Keisy and Daniela are the two most experienced candidates in the group and they complement each other well on paper. Keisy is stronger on the content-to-subscriber pipeline, the scripting and hook writing side, and restricted-ad industry experience. Daniela is stronger on visual identity, brand system design, and static creative production. If the priority is someone who can build the full content machine from social to email to revenue, Keisy is the better pick. If the priority is building a world-class brand aesthetic first, Daniela has the edge. For this role as written, the pipeline matters more than the design system, which is why Keisy ranks higher.",
      },
      {
        heading: "Compared to Santiago",
        body: "Keisy brings more experience, deeper production skills, and the restricted-ad background that Santiago doesn't have. Santiago brings stronger metric evidence, ManyChat automation skills, and a lower price point. If you're choosing between the two, Keisy is the safer bet for day-one execution at the senior level this role demands. Santiago is the higher-upside, higher-risk choice who could grow into the role quickly but would need more ramp time.",
      },
      {
        heading: "Compared to Tomas",
        body: "These two are solving different problems. Keisy creates content, edits video, designs graphics, writes hooks, and publishes across platforms. Tomas builds AI workflows, content automation systems, and strategic frameworks. The role needs the first set of skills every day. Keisy is the clear fit here.",
      },
    ],
  },
  {
    sectionTitle: "Daniela",
    items: [
      {
        heading: "Compared to Keisy",
        body: "Daniela's design and brand identity work is stronger than Keisy's. She's built 13 brands from scratch and her visual system thinking is exactly what Frizo and LGO need long term. Where she falls behind is on the content-to-email pipeline, scripting, and hook writing, which are the daily engine of this role. She also doesn't have any experience in restricted-ad industries. If Daniela can demonstrate strong scripting and video editing skills in a practical test, the gap between her and Keisy narrows significantly.",
      },
      {
        heading: "Compared to Santiago",
        body: "Daniela has significantly more career experience and much stronger design skills. Santiago has better metric evidence and the ManyChat automation experience that Daniela doesn't have. They're strong in different areas: Daniela on the brand and visual side, Santiago on the data and conversion infrastructure side. For the full scope of this role, Daniela's broader experience gives her the edge, but Santiago's subscriber conversion instincts are more naturally aligned with the role's #1 metric.",
      },
      {
        heading: "Compared to Tomas",
        body: "Daniela has the design, brand strategy, and content production skills that the role requires daily. Tomas has the AI automation depth that Daniela doesn't, but lacks the visual production capability entirely. For this role, Daniela is the stronger fit. Tomas would complement her well on a larger team, but as a solo hire, Daniela covers more of what you need.",
      },
    ],
  },
  {
    sectionTitle: "Santiago",
    items: [
      {
        heading: "Compared to Keisy",
        body: "Santiago has the numbers to back up his work: 406% more views, 544% more reach, 32% follower growth, all tracked and documented. Keisy has more experience, stronger production skills, and direct restricted-ad category experience. Santiago's ManyChat automation and conversion instincts are genuinely strong, and at $18/hr his cost-to-potential is the best in the group. But Keisy is more ready to operate at the senior level this role demands from day one.",
      },
      {
        heading: "Compared to Daniela",
        body: "Santiago thinks more naturally in subscriber conversion and metrics than Daniela does. Daniela is a much stronger designer and has more career depth. Santiago's production tools are more limited (CapCut and Canva vs. Daniela's full Adobe and Figma stack). For someone who needs to build both the brand identity and the content engine simultaneously, Daniela covers more ground. For someone focused purely on growing the email list through content, Santiago's instincts are sharper.",
      },
      {
        heading: "Compared to Tomas",
        body: "Santiago and Tomas are both earlier in their careers, but Santiago's skills are actually aligned with what this role does every day. He manages TikTok and Instagram content, builds lead funnels, tracks KPIs weekly, and optimizes based on data. Tomas builds AI systems and writes B2B content. For this specific role, Santiago is the much stronger fit despite having fewer total years of experience.",
      },
    ],
  },
  {
    sectionTitle: "Tomas",
    items: [
      {
        heading: "Compared to Keisy",
        body: "Tomas is stronger on AI automation and strategic systems thinking. Keisy is stronger on everything else this role requires: video editing, graphic design, hook writing, subscriber conversion strategy, founder coaching, and restricted-ad experience. As a solo content operator for Frizo and LGO, Keisy covers the role. Tomas would need production support from day one.",
      },
      {
        heading: "Compared to Daniela",
        body: "Tomas has deeper AI capability and more structured strategic thinking. Daniela has the design skills, brand identity experience, and content production ability that the role needs daily. Tomas's B2B background means he'd need to learn DTC social content production from scratch, while Daniela has already been doing agency-level social media work for years.",
      },
      {
        heading: "Compared to Santiago",
        body: "Both are from Colombia and available immediately. Santiago has less total experience but his skills map directly to the role: social media content management, platform-native publishing, ManyChat lead funnels, and documented growth metrics. Tomas has more sophisticated AI and strategy skills but hasn't done the daily content production work this role requires. For this hire, Santiago is the better fit.",
      },
    ],
  },
];
 

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function tone(score) {
  if (score >= 7) return "good";
  if (score >= 5) return "mid";
  return "warn";
}

function safeHref(name) {
  return PROFILE_LINKS[name] || "#";
}

function safeHeadshot(name) {
  return HEADSHOTS[name] || "";
}

function NewTabLink({ href, className, children }) {
  return (
    <a className={className} href={href} target="_blank" rel="noreferrer noopener">
      {children}
    </a>
  );
}

function SectionShell({ kicker, title, subtitle, right, children }) {
  return (
    <section className="block">
      <div className="blockTop">
        <div className="blockTopLeft">
          {kicker && <div className="blockKicker">{kicker}</div>}
          <div className="blockTitleRow">
            <h2 className="blockTitle">{title}</h2>
            {right ? <div className="blockRight">{right}</div> : null}
          </div>
          {subtitle ? <div className="blockSubtitle">{subtitle}</div> : null}
        </div>
      </div>
      <div className="blockBody">{children}</div>
    </section>
  );
}

export default function CandidateFitDashboardChad() {
  const [openSections, setOpenSections] = useState({
    "Juan Esteban Comparison": true,
  });

  const candidateKeys = useMemo(
    () => ["Keisy", "Daniela", "Santiago", "Tomas"],
    []
  );

  const toggleSection = (title) =>
    setOpenSections((p) => ({ ...p, [title]: !p[title] }));

  return (
    <div className="page">
      <div className="bgGlow" aria-hidden="true" />

      <div className="container">
        <header className="hero">
          <div className="heroKicker">Frizo / LGO Brands</div>
          <h1 className="heroTitle">Content Producer — Candidate Fit Dashboard</h1>
          <p className="heroSub">
            Ranked overview, dimension scoring, and interview-based fit notes.
          </p>
        </header>

        {/* SECTION 1 */}
        <SectionShell
          kicker="Section 01"
          title="Comparative Fit Summary"
          subtitle="A high-level snapshot of each candidate, ranked from strongest overall fit to least immediate fit."
        >
          <div className="grid4">
            {rankedOverview.map((c) => (
              <article
                key={c.name}
                className={`card rankCard ${c.rank === 1 ? "cardHero" : ""}`}
              >
                <div className="rankRow">
                  <div className="rankBadge">#{c.rank}</div>
                  <div className="rankName">{c.name}</div>
                  <div className="rankSpacer" />
                </div>

                <div className="rankAvatarWrap">
                  <img
                    className="rankAvatarBig"
                    src={safeHeadshot(c.name)}
                    alt={`${c.name} headshot`}
                    loading="lazy"
                  />
                </div>

                <div className="rankTitle">{c.title}</div>
                <p className="rankBody">{c.summary}</p>

                <div className="cardFooter">
                  <NewTabLink className="selectBtn" href={safeHref(c.name)}>
                    Open profile →
                  </NewTabLink>
                </div>
              </article>
            ))}
          </div>
        </SectionShell>

        {/* SECTION 2 */}
        <SectionShell
          kicker="Section 02"
          title="Score Comparison"
          subtitle="Scores per dimension (1–10)."
        >
          <div className="card tableCard">
            <div className="tableWrap" role="region" aria-label="Score table">
              <table className="table">
                <thead>
                  <tr>
                    <th className="th dimCol">Dimension</th>
                    {candidateKeys.map((k) => (
                      <th key={k} className="th candCol">
                        <div className="thCand">
                          <div className="thName">{k}</div>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {dimensions.map((d, idx) => (
                    <tr key={d.id} className="tr">
                      <td className="td dimCell">
                        <div className="dimLabel">
                          {d.id}. {d.label}
                        </div>
                      </td>

                      {candidateKeys.map((k) => {
                        const s = scores[k][idx];
                        const pct = clamp((s / 10) * 100, 0, 100);
                        const t = tone(s);

                        return (
                          <td key={`${d.id}-${k}`} className="td scoreCell">
                            <div className="scoreBox">
                              <div className="scoreTop">
                                <div className="scoreNum">{s}</div>
                                <div className={`scoreDot ${t}`} aria-hidden="true" />
                              </div>
                              <div className="bar">
                                <div
                                  className={`barFill ${t}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}

                </tbody>
              </table>
            </div>
          </div>
        </SectionShell>

        {/* SECTION 3 */}
        <SectionShell
          kicker="Section 03"
          title="Candidate Comparisons"
          subtitle="Interview-based notes comparing each candidate against the others."
          right={<span className="tinyNote">Expandable details</span>}
        >
          <div className="card compareShell">
            <div className="compareShellInner">
              {comparisons.map((sec, i) => {
                const isOpen = !!openSections[sec.sectionTitle];
                return (
                  <div
                    key={sec.sectionTitle}
                    className={`rowAcc ${i === 0 ? "first" : ""}`}
                  >
                    <button
                      type="button"
                      className="rowAccHead"
                      onClick={() => toggleSection(sec.sectionTitle)}
                      aria-expanded={isOpen}
                    >
                      <div className="rowAccLeft">
                        <div className="rowAccTitle">{sec.sectionTitle}</div>
                        <div className="rowAccHint">Click to expand</div>
                      </div>
                      <div className="rowAccIcon" aria-hidden="true">
                        {isOpen ? "–" : "+"}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="rowAccBody">
                        {sec.items.map((it) => (
                          <div key={it.heading} className="compareRow">
                            <div className="compareHeading">{it.heading}</div>
                            <div className="compareText">{it.body}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </SectionShell>
      </div>

      <style>{`
        :root {
          --aa-navy: #01014C;
          --aa-navy2: #0A0A80;
          --aa-cyan: #36D1FF;
          --aa-cream: #FFFDFB;
          --aa-orange: #EB5D2A;
        }

        html, body { background: var(--aa-navy); }

        .page {
          min-height: 100vh;
          padding: 26px 18px 60px;
          position: relative;
          overflow: hidden;
          color: #eaf1ff;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial;
          font-size: 16px;
        }

        .bgGlow {
          position: absolute;
          inset: -220px;
          background:
            radial-gradient(900px 620px at 18% 18%, rgba(54,209,255,0.22), transparent 62%),
            radial-gradient(760px 520px at 78% 22%, rgba(10,10,128,0.22), transparent 58%),
            radial-gradient(980px 780px at 52% 92%, rgba(54,209,255,0.12), transparent 62%),
            radial-gradient(900px 700px at 50% 50%, rgba(255,253,251,0.04), transparent 65%);
          pointer-events: none;
          z-index: 0;
        }

        .container { max-width: 1200px; margin: 0 auto; position: relative; z-index: 1; }

        /* HERO */
        .hero { margin-bottom: 18px; }
        .heroKicker {
          font-size: 13px; letter-spacing: .14em; text-transform: uppercase;
          color: rgba(234,241,255,.78);
          margin-bottom: 10px;
        }
        .heroTitle {
          margin: 0;
          font-size: 36px;
          font-weight: 900;
          line-height: 1.06;
          background: linear-gradient(90deg, var(--aa-cream) 0%, rgba(54,209,255,.95) 55%, rgba(255,253,251,.85) 100%);
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
        }
        .heroSub {
          margin: 12px 0 0;
          max-width: 72ch;
          color: rgba(234,241,255,.78);
          font-size: 15px;
          line-height: 1.65;
        }

        /* SECTION SHELLS */
        .block {
          margin-top: 14px;
          border-radius: 26px;
          border: 1px solid rgba(54,209,255,.22);
          background: rgba(255,255,255,.055);
          box-shadow: 0 22px 70px rgba(0,0,0,.34);
          backdrop-filter: blur(12px);
          overflow: hidden;
        }

        .blockTop {
          padding: 16px 16px 14px;
          background:
            radial-gradient(900px 200px at 10% 0%, rgba(54,209,255,.18), transparent 55%),
            radial-gradient(700px 220px at 90% 0%, rgba(10,10,128,.16), transparent 55%),
            rgba(0,0,0,.08);
          border-bottom: 1px solid rgba(54,209,255,.16);
        }

        .blockKicker {
          font-size: 13px;
          letter-spacing: .12em;
          text-transform: uppercase;
          color: rgba(234,241,255,.70);
          margin-bottom: 8px;
        }

        .blockTitleRow {
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
        }

        .blockTitle {
          margin: 0;
          font-size: 20px;
          font-weight: 950;
          letter-spacing: .01em;
          color: rgba(245,248,255,.96);
        }

        .blockSubtitle {
          margin-top: 10px;
          color: rgba(234,241,255,.76);
          font-size: 14px;
          line-height: 1.7;
          max-width: 95ch;
        }

        .blockBody { padding: 14px 14px 16px; }
        .tinyNote { font-size: 13px; color: rgba(234,241,255,.70); }

        /* CARDS */
        .card {
          border-radius: 22px;
          background: rgba(255,255,255,.065);
          border: 1px solid rgba(54,209,255,.18);
          box-shadow: 0 18px 60px rgba(0,0,0,.26);
          backdrop-filter: blur(12px);
        }

        .selectBtn {
          width: 100%;
          font-weight: 900;
          border-radius: 16px;
          background: var(--aa-cyan);
          color: var(--aa-navy);
          border: 1px solid rgba(54,209,255,.55);
          box-shadow: 0 0 22px rgba(54,209,255,.35);
          padding: 11px 14px;
          font-size: 14px;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: transform .12s ease, background .12s ease, box-shadow .12s ease;
        }
        .selectBtn:hover {
          background: #5FE2FF;
          box-shadow: 0 0 30px rgba(54,209,255,.55);
          transform: translateY(-1px);
        }

        /* SECTION 1 — 4-col grid */
        .grid4 {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        @media (max-width: 1100px) { .grid4 { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 640px)  { .grid4 { grid-template-columns: 1fr; } }

        .rankCard {
          padding: 14px;
          display: flex;
          flex-direction: column;
          min-height: 292px;
          position: relative;
          overflow: hidden;
        }
        .cardHero { border-color: rgba(54,209,255,.42); }
        .cardHero:before {
          content: "";
          position: absolute;
          inset: 0;
          background: radial-gradient(520px 320px at 18% 0%, rgba(54,209,255,.18), transparent 58%);
          pointer-events: none;
        }

        .rankRow { display: flex; align-items: center; gap: 10px; position: relative; z-index: 1; }
        .rankBadge {
          width: 36px; height: 36px;
          display: grid; place-items: center;
          border-radius: 12px;
          background: rgba(0,0,0,.16);
          border: 1px solid rgba(54,209,255,.22);
          font-weight: 900;
          font-size: 14px;
        }
        .rankName { font-weight: 950; font-size: 16px; }
        .rankSpacer { flex: 1; }

        .rankAvatarWrap {
          display: grid;
          place-items: center;
          margin-top: 12px;
          margin-bottom: 6px;
          position: relative;
          z-index: 1;
        }
        .rankAvatarBig {
          width: 82px; height: 82px;
          border-radius: 999px;
          object-fit: cover;
          border: 3px solid rgba(54,209,255,.85);
          background: rgba(255,255,255,.06);
          box-shadow: 0 16px 40px rgba(0,0,0,.35), 0 0 0 10px rgba(54,209,255,.10);
        }

        .rankTitle {
          margin-top: 10px;
          font-size: 13px;
          font-weight: 950;
          color: rgba(245,248,255,.94);
          position: relative;
          z-index: 1;
        }
        .rankBody {
          margin: 8px 0 0;
          font-size: 13px;
          line-height: 1.7;
          color: rgba(234,241,255,.78);
          position: relative;
          z-index: 1;
        }
        .cardFooter { margin-top: auto; padding-top: 12px; position: relative; z-index: 1; }

        /* SECTION 2 TABLE */
        .tableCard { padding: 14px; }
        .tableWrap {
          overflow: auto;
          border-radius: 16px;
          border: 1px solid rgba(54,209,255,.14);
          background: rgba(0,0,0,.10);
        }
        table { width: 100%; border-collapse: separate; border-spacing: 0; min-width: 860px; }
        .th {
          position: sticky; top: 0; z-index: 2;
          background: rgba(1,1,76,.92);
          border-bottom: 1px solid rgba(54,209,255,.16);
          text-align: left;
          padding: 12px;
          font-size: 12px;
          letter-spacing: .08em;
          text-transform: uppercase;
          color: rgba(234,241,255,.72);
        }
        .dimCol { width: 320px; }
        .candCol { width: 140px; }
        .td { padding: 12px; border-bottom: 1px solid rgba(54,209,255,.10); vertical-align: top; }
        .dimLabel { font-size: 14px; color: rgba(245,248,255,.88); line-height: 1.6; }

        .thCand { display: flex; align-items: center; justify-content: flex-start; }
        .thName { font-weight: 950; letter-spacing: .01em; color: rgba(245,248,255,.92); font-size: 13px; }

        .scoreBox {
          border-radius: 14px;
          padding: 10px;
          border: 1px solid rgba(54,209,255,.12);
          background: rgba(255,255,255,.05);
        }
        .scoreTop { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 8px; }
        .scoreNum { font-size: 20px; font-weight: 950; }
        .scoreDot {
          width: 10px; height: 10px;
          border-radius: 999px;
          border: 1px solid rgba(255,255,255,.26);
        }
        .scoreDot.good { background: rgba(60,245,128,0.7); }
        .scoreDot.mid  { background: rgba(238,247,72,0.72); }
        .scoreDot.warn { background: rgba(255,184,70,0.75); }

        .bar {
          height: 9px;
          border-radius: 999px;
          background: rgba(255,255,255,.07);
          border: 1px solid rgba(54,209,255,.10);
          overflow: hidden;
        }
        .barFill { height: 100%; border-radius: 999px; }
        .barFill.good { background: rgba(60,245,128,0.7); }
        .barFill.mid  { background: rgba(238,247,72,0.72); }
        .barFill.warn { background: rgba(255,184,70,0.75); }

        /* Average row */
        .avgRow { background: rgba(54,209,255,.04); }
        .avgLabel { font-weight: 900; color: rgba(245,248,255,.96); }
        .avgNum { font-size: 22px; }

        /* SECTION 3 COMPARISONS */
        .compareShell { padding: 0; }
        .compareShellInner { padding: 8px; }

        .rowAcc {
          border-radius: 18px;
          border: 1px solid rgba(54,209,255,.14);
          background: rgba(0,0,0,.08);
          margin: 10px 6px;
          overflow: hidden;
        }
        .rowAccHead {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          padding: 14px;
          background: transparent;
          border: none;
          color: inherit;
          cursor: pointer;
        }
        .rowAccLeft { display: flex; flex-direction: column; gap: 4px; }
        .rowAccTitle { font-weight: 950; letter-spacing: .01em; font-size: 15px; }
        .rowAccHint { font-size: 13px; color: rgba(234,241,255,.65); }

        .rowAccIcon {
          width: 38px; height: 38px;
          border-radius: 12px;
          display: grid; place-items: center;
          border: 1px solid rgba(54,209,255,.18);
          background: rgba(255,255,255,.05);
          font-size: 18px;
          line-height: 1;
        }

        .rowAccBody { padding: 0 14px 14px; }

        .compareRow {
          display: grid;
          grid-template-columns: 210px 1fr;
          gap: 12px;
          padding: 12px 0;
          border-top: 1px solid rgba(54,209,255,.10);
        }
        @media (max-width: 720px) { .compareRow { grid-template-columns: 1fr; } }
        .compareHeading { font-weight: 950; color: rgba(245,248,255,.92); font-size: 14px; }
        .compareText { color: rgba(234,241,255,.78); line-height: 1.75; font-size: 14px; }
      `}</style>
    </div>
  );
}
